// src/composables/usePayrollCalculations.js
import { computed } from 'vue'

/**
 * Calculate OB bonus for a user for a given month.
 * @param {Object} user - The user object (must have payroll)
 * @param {Object} obHours - { ob1_hours, ob2_hours, ob3_hours }
 * @param {Object} obCoefficients - { ob1, ob2, ob3 }
 * @returns {number}
 */
export function calculateOBBonus(user, obHours, obCoefficients) {
  if (!user?.payroll?.hourly_salary || !obHours) return 0
  const hourlyRate = parseFloat(user.payroll.hourly_salary || 0)
  const ob1Bonus = parseFloat(obHours.ob1_hours || 0) * hourlyRate * (obCoefficients?.ob1 ?? 0.3)
  const ob2Bonus = parseFloat(obHours.ob2_hours || 0) * hourlyRate * (obCoefficients?.ob2 ?? 0.5)
  const ob3Bonus = parseFloat(obHours.ob3_hours || 0) * hourlyRate * (obCoefficients?.ob3 ?? 1.0)
  return ob1Bonus + ob2Bonus + ob3Bonus
}

/**
 * Calculate sick leave sequences and deductions for a user.
 * @param {Array} sickLeaveDays - Array of sick leave dates (sorted chronologically)
 * @param {Array} workDays - Array of work days in the month (dates with time entries or shifts)
 * @param {Object} shiftHoursByDate - Map of date to shift hours { '2025-10-29': 8 }
 * @returns {Object} - Map of date to deduction percentage (1.0 = 100% deduction, 0.2 = 20% deduction)
 */
export function calculateSickLeaveDeductions(sickLeaveDays, workDays, shiftHoursByDate = {}) {
  if (!sickLeaveDays || sickLeaveDays.length === 0) return {}
  
  const deductions = {}
  const sortedSickDays = [...sickLeaveDays].sort()
  const workDaySet = new Set(workDays || [])
  
  let sequenceCount = 0
  let lastSickDay = null
  
  sortedSickDays.forEach(sickDay => {
    const currentDate = new Date(sickDay)
    
    // Check if sequence continues (no work days between last sick day and this one)
    if (lastSickDay) {
      const lastDate = new Date(lastSickDay)
      let hasWorkedBetween = false
      
      // Check all days between lastSickDay and current sickDay
      for (let d = new Date(lastDate); d < currentDate; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0]
        if (workDaySet.has(dateStr)) {
          hasWorkedBetween = true
          break
        }
      }
      
      if (hasWorkedBetween) {
        // Reset sequence - worked at least one day between sick days
        sequenceCount = 0
      }
    }
    
    sequenceCount++
    
    // First sick day in sequence = 100% of shift hours deducted
    // Days 2-7 in sequence = 20% of shift hours deducted (80% paid)
    if (sequenceCount === 1) {
      deductions[sickDay] = {
        deductionRate: 1.0, // 100% deduction
        shiftHours: shiftHoursByDate[sickDay] || 0
      }
    } else if (sequenceCount <= 7) {
      deductions[sickDay] = {
        deductionRate: 0.2, // 20% deduction (80% paid)
        shiftHours: shiftHoursByDate[sickDay] || 0
      }
    } else {
      // After day 7, assume full pay (0% deduction)
      deductions[sickDay] = {
        deductionRate: 0, // 0% deduction (100% paid)
        shiftHours: shiftHoursByDate[sickDay] || 0
      }
    }
    
    lastSickDay = sickDay
  })
  
  return deductions
}

/**
 * Calculate the monthly salary for a user based on employment_type.
 * @param {Object} user - The user object (must have payroll)
 * @param {number} assignedHours - Total assigned/actual worked hours for the month
 * @param {Object} obHours - { ob1_hours, ob2_hours, ob3_hours }
 * @param {Object} obCoefficients - { ob1, ob2, ob3 }
 * @param {Array} sickLeaveDays - Array of sick leave dates for the user this month
 * @param {Array} workDays - Array of work days (dates with time entries)
 * @param {Object} shiftHoursByDate - Map of date to shift hours { '2025-10-29': 8 }
 * @returns {number} - The total monthly salary (base + OB - sick leave deductions)
 */
export function calculateMonthlySalary(user, assignedHours, obHours, obCoefficients, sickLeaveDays = [], workDays = [], shiftHoursByDate = {}) {
  const type = user?.payroll?.employment_type
  const payroll = user?.payroll || {}
  const hourlyRate = parseFloat(payroll.hourly_salary || 0)
  let baseSalary = 0
  let obBonus = calculateOBBonus(user, obHours, obCoefficients)

  if (type === 'call_in') {
    // Call-in: hourly salary × assigned hours + OB
    baseSalary = hourlyRate * (assignedHours || 0)
  } else if (type === 'part_time' || type === 'full_time') {
    // Part-time/full-time: monthly salary (will be reduced by sick leave)
    baseSalary = parseFloat(payroll.monthly_salary || 0)
  } else {
    // Fallback: try monthly salary, then hourly
    if (payroll.monthly_salary) {
      baseSalary = parseFloat(payroll.monthly_salary)
    } else if (payroll.hourly_salary) {
      baseSalary = hourlyRate * (assignedHours || 0)
    }
  }
  
  // Apply sick leave logic for ALL worker types
  // Swedish sick leave rules:
  // Day 1: 100% of shift pay - karensavdrag (backend calculates karensavdrag)
  // Day 2: 80% of shift pay - remaining karensavdrag from day 1 (if exists, backend tracks this)
  // Days 3-14: 80% of shift pay (no more karensavdrag)
  // Day 15+: 0% (Försäkringskassan pays, not employer)
  //
  // For monthly workers: we need to SUBTRACT normal pay for sick days, then ADD sick pay
  // For call-in workers: baseSalary already excludes sick days (assignedHours doesn't include them)
  let sickLeaveAdjustment = 0
  if (sickLeaveDays && sickLeaveDays.length > 0) {
    let totalNormalPay = 0 // What they would have earned if healthy
    let totalSickPay = 0 // What they actually get while sick
    let totalKarensavdrag = 0
    
    sickLeaveDays.forEach(sickLeave => {
      // sickLeave can be either an object or a date string (legacy)
      if (typeof sickLeave === 'object') {
        // Get shift hours for each day in this sick leave period
        const startDate = new Date(sickLeave.start_date)
        const endDate = new Date(sickLeave.end_date)
        
        let dayNumber = 1
        // Iterate through each day in the sick leave period
        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          const dateStr = d.toISOString().split('T')[0]
          const shiftHours = shiftHoursByDate[dateStr] || 0
          
          if (shiftHours > 0) {
            // Calculate normal pay for this day (what they would have earned)
            totalNormalPay += shiftHours * hourlyRate
            
            if (dayNumber === 1) {
              // Day 1: 100% of shift pay
              totalSickPay += shiftHours * hourlyRate
            } else if (dayNumber >= 2 && dayNumber <= 14) {
              // Days 2-14: 80% of shift pay
              totalSickPay += shiftHours * hourlyRate * 0.80
            }
            // Day 15+: 0% (Försäkringskassan pays)
          }
          dayNumber++
        }
        
        // Add karensavdrag deduction (backend handles day 1 + day 2 remainder)
        if (sickLeave.deduction_amount) {
          totalKarensavdrag += parseFloat(sickLeave.deduction_amount)
        }
      }
    })
    
    // For ALL workers: replace normal pay with sick pay, then subtract karensavdrag
    // Adjustment = sickPay - normalPay - karensavdrag
    // This is negative because sick pay < normal pay (80% or less)
    sickLeaveAdjustment = totalSickPay - totalNormalPay - totalKarensavdrag
  }
  
  // Return: base salary + sick leave adjustment + OB bonus
  const finalSalary = baseSalary + sickLeaveAdjustment + obBonus
  return finalSalary
}

/**
 * Utility composable for payroll calculations.
 * Usage: import { usePayrollCalculations } from '@/composables/usePayrollCalculations'
 */
export function usePayrollCalculations() {
  /**
   * Calculate the base salary for a user for a given month.
   * @param {Object} user - The user object (must have payroll)
   * @param {Object} monthlyAssignedHours - Map of userId to assigned hours (optional, for call_in)
   * @returns {number}
   */
  function calculateBaseSalary(user, monthlyAssignedHours = {}) {
    const type = user?.payroll?.employment_type
    const payroll = user?.payroll || {}
    if (type === 'call_in') {
      return parseFloat(payroll.hourly_salary || 0) * (monthlyAssignedHours[user.id] || 0)
    } else if (type === 'part_time' || type === 'full_time') {
      return parseFloat(payroll.monthly_salary || 0)
    }
    if (payroll.monthly_salary) return parseFloat(payroll.monthly_salary)
    if (payroll.hourly_salary) return parseFloat(payroll.hourly_salary) * (monthlyAssignedHours[user.id] || 0)
    return 0
  }

  /**
   * Calculate the gross salary for a user for a given month.
   * @param {Object} user
   * @param {Object} monthlyAssignedHours - Map of userId to assigned hours
   * @param {Object} monthlyOBHours - Map of userId to OB hours
   * @param {Object} obCoefficients - { ob1, ob2, ob3 }
   * @param {Object} authStore - (optional) for company fallback
   * @param {Object} monthlySickLeaves - (optional) Map of userId to array of sick leave dates
   * @param {Object} monthlyWorkDays - (optional) Map of userId to array of work day dates
   * @param {Object} monthlyShiftHours - (optional) Map of userId to map of dates to shift hours { userId: { '2025-10-29': 8 } }
   * @returns {number}
   */
  function calculateGrossSalary(user, monthlyAssignedHours = {}, monthlyOBHours = {}, obCoefficients = {}, authStore = null, monthlySickLeaves = {}, monthlyWorkDays = {}, monthlyShiftHours = {}) {
    const assignedHours = monthlyAssignedHours[user.id] || 0
    const obHours = monthlyOBHours[user.id] || { ob1_hours: 0, ob2_hours: 0, ob3_hours: 0 }
    const sickLeaveDays = monthlySickLeaves[user.id] || []
    const workDays = monthlyWorkDays[user.id] || []
    const shiftHoursByDate = monthlyShiftHours[user.id] || {}
    
    // Use company OB coefficients if available
    let company = user.company
    if (!company && authStore) company = authStore.company
    const coeffs = {
      ob1: Number(company?.overtime_rate1) ?? 0.3,
      ob2: Number(company?.overtime_rate2) ?? 0.5,
      ob3: Number(company?.overtime_rate3) ?? 1.0,
      ...obCoefficients
    }
    return calculateMonthlySalary(user, assignedHours, obHours, coeffs, sickLeaveDays, workDays, shiftHoursByDate)
  }

  /**
   * Calculate employee tax for a user (from cache or 0 if not available)
   * @param {Object} user
   * @param {Object} userTaxCache - Map of userId to { [monthKey]: { grossSalary, tax } }
   * @param {Date} selectedDate
   * @returns {number}
   */
  function calculateEmployeeTax(user, userTaxCache = {}, selectedDate = new Date()) {
    if (!user || !user.id) return 0
    const currentMonth = selectedDate.getMonth()
    const currentYear = selectedDate.getFullYear()
    const cacheKey = `${currentYear}-${currentMonth}`
    const userCache = userTaxCache[user.id]
    if (!userCache) return 0
    const monthCache = userCache[cacheKey]
    return monthCache?.tax || 0
  }

  /**
   * Async function to fetch and cache tax for a user
   * @param {Object} user
   * @param {Function} calculateGrossSalaryFn - function to calculate gross salary
   * @param {Object} userTaxCache - ref object to update
   * @param {Date} selectedDate
   * @param {Function} calculatePreliminaryTax - API call
   */
  async function fetchAndCacheUserTax(user, calculateGrossSalaryFn, userTaxCache, selectedDate, calculatePreliminaryTax) {
    if (!user || !user.id) return
    try {
      const grossSalary = calculateGrossSalaryFn(user)
      const currentMonth = selectedDate.getMonth()
      const currentYear = selectedDate.getFullYear()
      const cacheKey = `${currentYear}-${currentMonth}`
      if (!userTaxCache.value[user.id]) userTaxCache.value[user.id] = {}
      const monthCache = userTaxCache.value[user.id][cacheKey]
      if (monthCache && monthCache.grossSalary === grossSalary) return
      if (!grossSalary || grossSalary <= 0) {
        userTaxCache.value[user.id][cacheKey] = { tax: 0, grossSalary: 0 }
        return
      }
      const taxTable = user.payroll?.employee_tax_table?.toString() || '30'
      const taxResult = await calculatePreliminaryTax({
        grossSalary: grossSalary,
        year: currentYear.toString(),
        taxTable: taxTable,
        paymentPeriod: '30B'
      })
      userTaxCache.value[user.id][cacheKey] = {
        tax: taxResult.preliminaryTax,
        grossSalary: grossSalary
      }
    } catch (error) {
      // Fallback to 30% estimate
      const grossSalary = calculateGrossSalaryFn(user)
      const fallbackTax = grossSalary * 0.30
      const currentMonth = selectedDate.getMonth()
      const currentYear = selectedDate.getFullYear()
      const cacheKey = `${currentYear}-${currentMonth}`
      if (!userTaxCache.value[user.id]) userTaxCache.value[user.id] = {}
      userTaxCache.value[user.id][cacheKey] = {
        tax: fallbackTax,
        grossSalary: grossSalary
      }
    }
  }

  /**
   * Calculate total payroll for a user (base + OB bonus)
   * @param {Object} user
   * @param {Function} calculateOBBonusFn
   * @returns {number}
   */
  function calculateTotalPayroll(user, calculateOBBonusFn = calculateOBBonus) {
    try {
      const baseSalary = parseFloat(user.monthly_salary || 0)
      const obBonus = calculateOBBonusFn(user)
      return baseSalary + obBonus
    } catch (error) {
      return parseFloat(user.monthly_salary || 0)
    }
  }

  /**
   * Get total OB hours for a given OB type across users
   * @param {Array} users
   * @param {Object} monthlyOBHours
   * @param {string} obType
   * @returns {string}
   */
  function getTotalOBHours(users, monthlyOBHours, obType) {
    try {
      const total = users.reduce((sum, user) => {
        const userOB = monthlyOBHours[user.id]
        if (!userOB) return sum
        const hours = parseFloat(userOB[obType] || 0)
        return sum + hours
      }, 0)
      if (total === 0) return '-'
      return total.toFixed(2) + 'h'
    } catch (error) {
      return '-'
    }
  }

  /**
   * Get total payroll for all users
   * @param {Array} users
   * @param {Function} calculateTotalPayrollFn
   * @returns {number}
   */
  function getTotalPayroll(users, calculateTotalPayrollFn = calculateTotalPayroll) {
    try {
      return users.reduce((sum, user) => sum + calculateTotalPayrollFn(user), 0)
    } catch (error) {
      return 0
    }
  }

  return {
    calculateOBBonus,
    calculateMonthlySalary,
    calculateSickLeaveDeductions,
    calculateBaseSalary,
    calculateGrossSalary,
    calculateEmployeeTax,
    fetchAndCacheUserTax,
    calculateTotalPayroll,
    getTotalOBHours,
    getTotalPayroll
  }
}
